import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { HotelService } from '../services/hotelService';
import { DogService } from '../services/dogService';
import {
    filterBookingsBySearch,
    filterBookingsByStatus,
    sortBookings,
    calculateBookingStats,
    transformBookingFormData,
    validateBookingForm,
    formatInputDate,
    getMinDate,
    BOOKING_FILTERS
} from '../utils/hotelUtils';

/**
 * Custom hook for managing hotel bookings state and check-in operations
 * Encapsulates hotel booking management and check-in business logic and API interactions
 */
export function useHotelBookings(year = null) {
    const { authorizedFetch } = useAuth();

    // Core state
    const [bookings, setBookings] = useState([]);
    const [dogs, setDogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Counts span every booking, not just the loaded year, so the summary tiles
    // keep showing business-wide totals.
    const [globalStats, setGlobalStats] = useState(null);

    // UI state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState(BOOKING_FILTERS.ALL);
    const [sortBy, setSortBy] = useState('checkIn');
    const [sortDirection, setSortDirection] = useState('asc');

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [editingBooking, setEditingBooking] = useState(null);
    
    // Check-in form data (with vet info)
    const [formData, setFormData] = useState({
        dogId: '',
        checkIn: '',
        checkOut: '',
        notes: '',
        // Vet check-in fields
        vetWeight: '',
        vetTemperature: '',
        vetNotes: '',
        vetCheckedBy: ''
    });
    
    // Simple booking form data (no vet info)
    const [bookingFormData, setBookingFormData] = useState({
        dogId: '',
        checkIn: '',
        checkOut: '',
        notes: ''
    });
    
    // Checkout form data (with vet info)
    const [checkoutFormData, setCheckoutFormData] = useState({
        bookingId: '',
        vetWeight: '',
        vetTemperature: '',
        vetNotes: '',
        vetCheckedBy: ''
    });
    
    const [formErrors, setFormErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);

    /**
     * Load all bookings from the API
     */
    const loadBookings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const bookingsData = await HotelService.getAllBookings(authorizedFetch, year);
            setBookings(bookingsData);
        } catch (err) {
            console.error('Error loading bookings:', err);
            setError('Failed to load bookings. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [authorizedFetch, year]);

    /**
     * Load business-wide booking counts for the summary tiles
     */
    const loadStats = useCallback(async () => {
        try {
            setGlobalStats(await HotelService.getBookingStats(authorizedFetch));
        } catch (err) {
            // Fall back to counting whatever is loaded rather than blanking the tiles.
            console.error('Error loading booking stats:', err);
            setGlobalStats(null);
        }
    }, [authorizedFetch]);

    /**
     * Load all dogs from the API
     */
    const loadDogs = useCallback(async () => {
        try {
            const response = await DogService.getAllDogs(authorizedFetch, { limit: 1000 });
            const dogsData = response.data ? response.data : (Array.isArray(response) ? response : []);
            setDogs(dogsData);
        } catch (err) {
            console.error('Error loading dogs:', err);
            setError('Failed to load dogs. Please try again.');
        }
    }, [authorizedFetch]);

    /**
     * Initialize data on component mount
     */
    useEffect(() => {
        loadBookings();
        loadDogs();
        loadStats();
    }, [loadBookings, loadDogs, loadStats]);

    /**
     * Get filtered and sorted bookings based on current filters
     */
    const filteredBookings = useCallback(() => {
        let filtered = filterBookingsBySearch(bookings, searchTerm);
        filtered = filterBookingsByStatus(filtered, filterType);
        return sortBookings(filtered, sortBy, sortDirection);
    }, [bookings, searchTerm, filterType, sortBy, sortDirection]);

    /**
     * Get booking statistics
     */
    const bookingStats = useCallback(() => {
        // Prefer server-side counts (all bookings); fall back to the loaded page.
        return globalStats || calculateBookingStats(bookings);
    }, [globalStats, bookings]);

    /**
     * Handle form field changes
     */
    const handleFormChange = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear field-specific errors when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    }, [formErrors]);

    /**
     * Reset form to initial state
     */
    const resetForm = useCallback(() => {
        setFormData({
            dogId: '',
            checkIn: '',
            checkOut: '',
            notes: '',
            // Reset vet check-in fields
            vetWeight: '',
            vetTemperature: '',
            vetNotes: '',
            vetCheckedBy: ''
        });
        setFormErrors({});
        setEditingBooking(null);
        setShowForm(false);
    }, []);
    
    /**
     * Reset booking form
     */
    const resetBookingForm = useCallback(() => {
        setBookingFormData({
            dogId: '',
            checkIn: '',
            checkOut: '',
            notes: ''
        });
        setFormErrors({});
    }, []);
    
    /**
     * Reset checkout form
     */
    const resetCheckoutForm = useCallback(() => {
        setCheckoutFormData({
            bookingId: '',
            vetWeight: '',
            vetTemperature: '',
            vetNotes: '',
            vetCheckedBy: ''
        });
        setFormErrors({});
    }, []);
    
    /**
     * Handle booking form field changes
     */
    const handleBookingFormChange = useCallback((field, value) => {
        setBookingFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear field-specific errors when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    }, [formErrors]);
    
    /**
     * Handle checkout form field changes
     */
    const handleCheckoutFormChange = useCallback((field, value) => {
        setCheckoutFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear field-specific errors when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    }, [formErrors]);

    /**
     * Show form for creating new check-in
     */
    const showCreateForm = useCallback(() => {
        resetForm();
        setShowForm(true);
    }, [resetForm]);

    /**
     * Show form for editing existing check-in
     */
    const showEditForm = useCallback((booking) => {
        setFormData({
            dogId: booking.dog?._id || '',
            checkIn: formatInputDate(booking.checkIn),
            checkOut: formatInputDate(booking.checkOut),
            notes: booking.notes || '',
            // Populate vet check-in fields
            vetWeight: booking.vetCheckIn?.weight || '',
            vetTemperature: booking.vetCheckIn?.temperature || '',
            vetNotes: booking.vetCheckIn?.notes || '',
            vetCheckedBy: booking.vetCheckIn?.checkedBy || ''
        });
        setFormErrors({});
        setEditingBooking(booking);
        setShowForm(true);
    }, []);

    /**
     * Cancel form and reset state
     */
    const cancelForm = useCallback(() => {
        resetForm();
    }, [resetForm]);

    /**
     * Create a new check-in
     */
    const createBooking = useCallback(async (bookingData) => {
        try {
            setFormLoading(true);
            setFormErrors({});

            // Validate form data
            const validation = validateBookingForm(bookingData);
            if (!validation.isValid) {
                setFormErrors(validation.errors);
                return false;
            }

            // Transform and submit data
            const transformedData = transformBookingFormData(bookingData);
            const newBooking = await HotelService.createBooking(transformedData, authorizedFetch);

            // Update local state
            setBookings(prev => [...prev, newBooking]);
            resetForm();
            return true;
        } catch (err) {
            console.error('Error creating booking:', err);
            setError('Failed to create booking. Please try again.');
            return false;
        } finally {
            setFormLoading(false);
        }
    }, [authorizedFetch, resetForm]);

    /**
     * Update an existing check-in
     */
    const updateBooking = useCallback(async (bookingId, bookingData) => {
        try {
            setFormLoading(true);
            setFormErrors({});

            // Skip full validation for vet-only updates (no dogId/dates present)
            const isVetOnly = 'vetCheckIn' in bookingData && !bookingData.dogId;
            if (!isVetOnly) {
                const validation = validateBookingForm(bookingData);
                if (!validation.isValid) {
                    setFormErrors(validation.errors);
                    return false;
                }
            }

            // Transform and submit data
            const transformedData = isVetOnly ? bookingData : transformBookingFormData(bookingData);
            const updatedBooking = await HotelService.updateBooking(bookingId, transformedData, authorizedFetch);

            // Update local state
            setBookings(prev =>
                prev.map(booking =>
                    booking._id === bookingId ? updatedBooking : booking
                )
            );
            resetForm();
            return true;
        } catch (err) {
            console.error('Error updating booking:', err);
            setError('Failed to update booking. Please try again.');
            return false;
        } finally {
            setFormLoading(false);
        }
    }, [authorizedFetch, resetForm]);

    /**
     * Delete a check-in
     */
    const deleteBooking = useCallback(async (bookingId) => {
        try {
            setLoading(true);
            await HotelService.deleteBooking(bookingId, authorizedFetch);

            // Update local state
            setBookings(prev => prev.filter(booking => booking._id !== bookingId));
            return true;
        } catch (err) {
            console.error('Error deleting booking:', err);
            setError('Failed to delete booking. Please try again.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [authorizedFetch]);

    /**
     * Handle form submission
     */
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        let success;
        if (editingBooking) {
            success = await updateBooking(editingBooking._id, formData);
        } else {
            success = await createBooking(formData);
        }

        return success;
    }, [editingBooking, formData, createBooking, updateBooking]);

    /**
     * Clear any error messages
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Handle search input changes
     */
    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
    }, []);

    /**
     * Handle filter type changes
     */
    const handleFilterChange = useCallback((value) => {
        setFilterType(value);
    }, []);

    /**
     * Handle sort changes
     */
    const handleSortChange = useCallback((field) => {
        if (sortBy === field) {
            // Toggle direction if same field
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('asc');
        }
    }, [sortBy]);

    /**
     * Get dogs formatted for select options (all dogs)
     */
    const dogOptions = useCallback(() => {
        return dogs.map(dog => ({
            value: dog._id,
            label: `${dog.name} (${dog.breed}) - ${dog.owners?.[0]?.name || 'No Owner'}`
        }));
    }, [dogs]);
    
    /**
     * Get dogs that have bookings (for check-in dropdown)
     */
    const bookedDogOptions = useCallback(() => {
        // Get unique dogs from bookings that are 'booked' status (ready for check-in)
        const bookedDogs = bookings
            .filter(booking => booking.status === 'booked')
            .map(booking => booking.dog)
            .filter((dog, index, self) =>
                dog && self.findIndex(d => d && d._id === dog._id) === index // Remove duplicates
            );
            
        return bookedDogs.map(dog => ({
            value: dog._id,
            label: `${dog.name} (${dog.breed}) - ${dog.owners?.[0]?.name || 'No Owner'}`,
            dog: dog
        }));
    }, [bookings]);

    /**
     * Check in a dog — either against an existing 'booked' reservation, or as a
     * walk-in (no prior reservation), in which case a new booking is created
     * directly with status 'checked-in'.
     */
    const checkInExistingBooking = useCallback(async (formData) => {
        try {
            setFormLoading(true);
            setFormErrors({});

            // Validate form data
            const validation = validateBookingForm(formData);
            if (!validation.isValid) {
                setFormErrors(validation.errors);
                return false;
            }

            // Find an existing booking with 'booked' status for this dog, if any
            const existingBooking = bookings.find(booking =>
                booking.dog?._id === formData.dogId &&
                booking.status === 'booked'
            );

            // Build vet check-in info if provided
            let vetCheckIn;
            if (formData.vetWeight || formData.vetTemperature ||
                formData.vetNotes || formData.vetCheckedBy ||
                formData.vetMedication || formData.ownerSignature) {
                vetCheckIn = {
                    ...(formData.vetWeight && { weight: parseFloat(formData.vetWeight) }),
                    ...(formData.vetTemperature && { temperature: parseFloat(formData.vetTemperature) }),
                    ...(formData.vetNotes && { notes: formData.vetNotes.trim() }),
                    ...(formData.vetCheckedBy && { checkedBy: formData.vetCheckedBy.trim() }),
                    ...(formData.vetMedication && { medication: formData.vetMedication.trim() }),
                    ...(formData.ownerSignature && { ownerSignature: formData.ownerSignature }),
                };
            }

            let updatedBooking;
            if (existingBooking) {
                // Reservation exists — check it in
                const updateData = {
                    status: 'checked-in',
                    checkIn: formData.checkIn,
                    checkOut: formData.checkOut,
                    notes: formData.notes?.trim() || '',
                    ...(vetCheckIn ? { vetCheckIn } : {})
                };
                updatedBooking = await HotelService.updateBooking(existingBooking._id, updateData, authorizedFetch);
                setBookings(prev =>
                    prev.map(booking => booking._id === existingBooking._id ? updatedBooking : booking)
                );
            } else {
                // Walk-in — create the booking already checked in
                const createData = {
                    dog: formData.dogId,
                    checkIn: formData.checkIn,
                    checkOut: formData.checkOut,
                    notes: formData.notes?.trim() || '',
                    status: 'checked-in',
                    ...(vetCheckIn ? { vetCheckIn } : {})
                };
                updatedBooking = await HotelService.createBooking(createData, authorizedFetch);
                setBookings(prev => [...prev, updatedBooking]);
            }

            resetForm();
            return true;
        } catch (err) {
            console.error('Error checking in booking:', err);
            setError('Failed to check in. Please try again.');
            return false;
        } finally {
            setFormLoading(false);
        }
    }, [authorizedFetch, bookings, resetForm]);

    /**
     * Create a simple booking (no vet info)
     */
    const createSimpleBooking = useCallback(async (bookingData) => {
        try {
            setFormLoading(true);
            setFormErrors({});

            // Validate form data
            const validation = validateBookingForm(bookingData);
            if (!validation.isValid) {
                setFormErrors(validation.errors);
                return false;
            }

            const transformedData = {
                dog: bookingData.dogId,
                checkIn: bookingData.checkIn,
                checkOut: bookingData.checkOut,
                notes: bookingData.notes?.trim() || '',
                status: 'booked',
                ...(bookingData.reservationId ? { reservationId: bookingData.reservationId } : {}),
                ...(bookingData.vetCheckIn ? { vetCheckIn: bookingData.vetCheckIn } : {})
            };
            
            const newBooking = await HotelService.createBooking(transformedData, authorizedFetch);

            // Update local state
            setBookings(prev => [...prev, newBooking]);
            resetBookingForm();
            return true;
        } catch (err) {
            console.error('Error creating booking:', err);
            setError('Failed to create booking. Please try again.');
            return false;
        } finally {
            setFormLoading(false);
        }
    }, [authorizedFetch, resetBookingForm]);
    
    /**
     * Check out a guest with vet info
     */
    const checkOutGuest = useCallback(async (checkoutData) => {
        try {
            setFormLoading(true);
            setFormErrors({});

            // Find the booking to checkout
            const booking = bookings.find(b => b._id === checkoutData.bookingId);
            if (!booking) {
                setError('Booking not found');
                return false;
            }

            // Prepare update data with checkout vet info
            const updateData = {
                status: 'checked-out'
            };
            
            // Add vet checkout info if provided
            if (checkoutData.vetWeight || checkoutData.vetTemperature ||
                checkoutData.vetNotes || checkoutData.vetCheckedBy ||
                checkoutData.vetMedication || checkoutData.ownerSignature) {
                updateData.vetCheckOut = {
                    ...(checkoutData.vetWeight && { weight: parseFloat(checkoutData.vetWeight) }),
                    ...(checkoutData.vetTemperature && { temperature: parseFloat(checkoutData.vetTemperature) }),
                    ...(checkoutData.vetNotes && { notes: checkoutData.vetNotes.trim() }),
                    ...(checkoutData.vetCheckedBy && { checkedBy: checkoutData.vetCheckedBy.trim() }),
                    ...(checkoutData.vetMedication && { medication: checkoutData.vetMedication.trim() }),
                    ...(checkoutData.ownerSignature && { ownerSignature: checkoutData.ownerSignature }),
                };
            }
            
            const updatedBooking = await HotelService.updateBooking(checkoutData.bookingId, updateData, authorizedFetch);

            // Update local state
            setBookings(prev =>
                prev.map(b => b._id === checkoutData.bookingId ? updatedBooking : b)
            );
            resetCheckoutForm();
            return true;
        } catch (err) {
            console.error('Error checking out guest:', err);
            setError('Failed to check out guest. Please try again.');
            return false;
        } finally {
            setFormLoading(false);
        }
    }, [authorizedFetch, bookings, resetCheckoutForm]);
    
    /**
     * Check in or check out several dogs of one reservation together.
     * @param {Array<{bookingId: string, data: Object}>} updates - One PUT body per booking
     * @returns {Promise<string[]>} The bookingIds that failed; empty when all saved
     */
    const updateReservationBookings = useCallback(async (updates) => {
        try {
            setFormLoading(true);
            setFormErrors({});

            // Each dog saves on its own, so one rejection must not hide the
            // others that went through. Re-sending a saved one is harmless.
            const results = await Promise.allSettled(
                updates.map(({ bookingId, data }) => HotelService.updateBooking(bookingId, data, authorizedFetch))
            );

            const saved = new Map();
            const failed = [];
            results.forEach((result, i) => {
                if (result.status === 'fulfilled') saved.set(updates[i].bookingId, result.value);
                else {
                    console.error('Error updating reservation booking:', result.reason);
                    failed.push(updates[i].bookingId);
                }
            });

            setBookings(prev => prev.map(b => saved.get(b._id) || b));
            return failed;
        } finally {
            setFormLoading(false);
        }
    }, [authorizedFetch]);

    /**
     * Get currently checked-in bookings for checkout dropdown
     */
    const checkedInBookings = useCallback(() => {
        return bookings
            .filter(booking => booking.status === 'checked-in')
            .map(booking => ({
                value: booking._id,
                label: `${booking.dog?.name} (${booking.dog?.breed}) - ${booking.dog?.owners?.[0]?.name || 'No Owner'}`,
                booking: booking
            }));
    }, [bookings]);
    
    /**
     * Get minimum date for form inputs
     */
    const minDate = getMinDate();

    return {
        // Core data
        bookings: filteredBookings(),
        dogs,
        loading,
        error,
        bookingStats: bookingStats(),

        // UI state
        searchTerm,
        filterType,
        sortBy,
        sortDirection,

        // Check-in form state
        showForm,
        editingBooking,
        formData,
        formErrors,
        formLoading,
        
        // Booking form state
        bookingFormData,
        
        // Checkout form state
        checkoutFormData,
        
        // Options
        dogOptions: dogOptions(),
        bookedDogOptions: bookedDogOptions(),
        checkedInBookings: checkedInBookings(),
        minDate,

        // Actions
        loadBookings,
        loadDogs,
        handleSearchChange,
        handleFilterChange,
        handleSortChange,
        showCreateForm,
        showEditForm,
        cancelForm,
        handleFormChange,
        handleFormSubmit,
        deleteBooking,
        clearError,
        handleBookingFormChange,
        handleCheckoutFormChange,
        resetBookingForm,
        resetCheckoutForm,
        createSimpleBooking,
        updateBooking,
        checkInExistingBooking,
        checkOutGuest,
        updateReservationBookings,

        // Utility functions
        BOOKING_FILTERS
    };
}
